import {
  DevotionalTemplateConfig,
  DiscoverItemCard,
  DiscoverItemType,
  FeatureableDiscoverItem,
  FeatureableDiscoverItemDescriptor,
  HubResource,
  Hubs,
  HubSectionType,
  KitResource,
  Kits,
  Templates,
  TemplateType,
} from 'utils/gloo-types';
import BROADCAST_THUMBNAIL from 'assets/Broadcast_card.png';
import BROADCAST_BANNER from 'assets/Broadcast_banner.png';
import DOWNLOAD_THUMBNAIL from 'assets/Download_card.png';
import DOWNLOAD_BANNER from 'assets/Download_banner.png';
import KEYWORD_THUMBNAIL from 'assets/Keywords_card.png';
import KEYWORD_BANNER from 'assets/Keywords_banner.png';
import LINK_THUMBNAIL from 'assets/Link_card.png';
import LINK_BANNER from 'assets/Link_banner.png';
import KIT_THUMBNAIL from 'assets/Kit_card.png';
import SERIES_THUMBNAIL from 'assets/Series_card.png';
import SERIES_BANNER from 'assets/Series_banner.png';
import SURVEY_THUMBNAIL from 'assets/Survey_card.png';
import SURVEY_BANNER from 'assets/Survey_banner.png';
import { capitalize, replace } from 'lodash';
import InAppRoutes, { pushProgressive } from './router';
import { SelectOption } from './frontendTypes';

const getThumbnailWidthImageUrl = (imageUrl: string): string => {
  return imageUrl.indexOf('/upload/w_') >= 0 ? imageUrl : imageUrl.replace('/upload', '/upload/w_500');
};

function getThumbnailImageFromKit(kit: Partial<Kits>) {
  const thumbUrl = kit?.thumbnailImageUrl;
  if (thumbUrl) return getThumbnailWidthImageUrl(thumbUrl);

  const imageUrl = kit?.bannerImageUrl;
  if (imageUrl) return getThumbnailWidthImageUrl(imageUrl);

  return KIT_THUMBNAIL;
}

function getThumbnailImageFromTemplate(template: Partial<Templates>) {
  const thumbUrl = template.thumbnailImageUrl;
  if (thumbUrl) return getThumbnailWidthImageUrl(thumbUrl);

  const imageUrl = template.imageUrl;
  if (imageUrl) return getThumbnailWidthImageUrl(imageUrl);

  switch (template.type) {
    case TemplateType.BROADCAST:
      return BROADCAST_THUMBNAIL;
    case TemplateType.SERIES:
      return SERIES_THUMBNAIL;
    case TemplateType.LINK:
      return LINK_THUMBNAIL;
    case TemplateType.DOWNLOAD:
      return DOWNLOAD_THUMBNAIL;
    case TemplateType.SURVEY:
      return SURVEY_THUMBNAIL;
    case TemplateType.KEYWORD:
    default:
      return KEYWORD_THUMBNAIL;
  }
}

export function getImageOrThumbnailFromTemplate(template: Partial<Templates>) {
  const imageUrl = template.imageUrl;
  if (imageUrl) return getThumbnailWidthImageUrl(imageUrl);

  const thumbUrl = template.thumbnailImageUrl;
  if (thumbUrl) return getThumbnailWidthImageUrl(thumbUrl);

  switch (template.type) {
    case TemplateType.BROADCAST:
      return BROADCAST_BANNER;
    case TemplateType.SERIES:
      return SERIES_BANNER;
    case TemplateType.LINK:
      return LINK_BANNER;
    case TemplateType.DOWNLOAD:
      return DOWNLOAD_BANNER;
    case TemplateType.SURVEY:
      return SURVEY_BANNER;
    case TemplateType.KEYWORD:
    default:
      return KEYWORD_BANNER;
  }
}

export const discoverItemCardForTemplate = (template: Partial<Templates>): DiscoverItemCard => {
  const itemCard: DiscoverItemCard = {
    itemId: template.id || 'unpublished',
    discoverType: DiscoverItemType.TEMPLATE,
    metadata: template.metadata,
    shortDescription: template.shortDescription || '[SHORT DESCRIPTION]',
    tags: template.tags || [],
    templateType: template.type,
    thumbnailImageUrl: getThumbnailImageFromTemplate(template),
    title: template.title || '[TITLE]',
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  };
  if (itemCard.templateType === TemplateType.SERIES) {
    itemCard.devotionalType = (template.config as DevotionalTemplateConfig)?.devotionalType;
  }
  return itemCard;
};

export const discoverItemCardForHub = (hub: Hubs): DiscoverItemCard => ({
  itemId: hub.id ?? 'unpublished',
  discoverType: DiscoverItemType.HUB,
  metadata: {
    resourceCount: hub.sections.reduce(
      (resourceCount, section) =>
        section.type === HubSectionType.RESOURCE ? resourceCount + section.resourceIds.length : resourceCount,
      0,
    ),
    author: hub.author,
  },
  shortDescription: hub.shortDescription,
  tags: hub.tags ?? [],
  thumbnailImageUrl: hub.thumbnailImageUrl,
  title: hub.title,
  createdAt: hub.createdAt,
  updatedAt: hub.updatedAt,
});

export const discoverItemCardForKit = (kit: Partial<Kits>): DiscoverItemCard => ({
  itemId: kit.id || 'unpublished',
  discoverType: DiscoverItemType.KIT,
  metadata: kit.metadata,
  shortDescription: kit.shortDescription || '[SHORT DESCRIPTION]',
  tags: kit.tags || [],
  thumbnailImageUrl: getThumbnailImageFromKit(kit),
  title: kit.title || '[TITLE]',
  createdAt: kit.createdAt,
  updatedAt: kit.updatedAt,
});

export const sortFeatureableDiscoverItemsByTitle = (a: FeatureableDiscoverItem, b: FeatureableDiscoverItem) =>
  a.item.title.localeCompare(b.item.title);

export const discoverItemForFeatureableDiscoverItem = (featureableDiscoverItem: FeatureableDiscoverItem) => {
  switch (featureableDiscoverItem.type) {
    case DiscoverItemType.HUB:
      return discoverItemCardForHub(featureableDiscoverItem.item);
    case DiscoverItemType.KIT:
      return discoverItemCardForKit(featureableDiscoverItem.item);
  }
};

export const featureableDiscoverItemForHub = (hub: Hubs): FeatureableDiscoverItem => ({
  type: DiscoverItemType.HUB,
  item: hub,
});

export const featureableDiscoverItemForKit = (kit: Kits): FeatureableDiscoverItem => ({
  type: DiscoverItemType.KIT,
  item: kit,
});

export const kitResourceForKit = (resourceKit: Kits): KitResource => {
  return {
    type: DiscoverItemType.KIT,
    kit: resourceKit,
  } as KitResource;
};

export const kitResourceForTemplate = (resourceTemplate: Templates): KitResource => {
  return {
    type: DiscoverItemType.TEMPLATE,
    template: resourceTemplate,
  } as KitResource;
};

export const hubResourceForKit = (kit: Kits): HubResource => ({
  type: DiscoverItemType.KIT,
  kit,
});

export const hubResourceForTemplate = (template: Templates): HubResource => ({
  type: DiscoverItemType.TEMPLATE,
  template,
});

export const normalizedContentType = (rawContentType: string): string => {
  if (!rawContentType) return 'N/A';
  const ext = rawContentType.toUpperCase();
  // Supported extensions from:
  // https://cloudinary.com/documentation/image_transformations#supported_image_formats
  // https://cloudinary.com/documentation/video_manipulation_and_delivery#supported_video_formats
  // https://cloudinary.com/documentation/audio_transformations#supported_audio_formats
  switch (ext) {
    case 'AI':
    case 'AVIF':
    case 'GIF':
    case 'PNG':
    case 'WEBP':
    case 'BMP':
    case 'BW':
    case 'DJVU':
    case 'EPS':
    case 'FBX':
    case 'FLIF':
    case 'GLB':
    case 'GLTF':
    case 'HEIF':
    case 'ICO':
    case 'INDD':
    case 'JPE':
    case 'JPG':
    case 'JPEG':
    case 'JP2':
    case 'WDP':
    case 'JXR':
    case 'HDP':
    case 'OBJ':
    case 'PLY':
    case 'PSD':
    case 'ARW':
    case 'CR2':
    case 'SVG':
    case 'TGA':
    case 'TIFF':
    case 'U3MA':
    case 'USDZ':
      return `Image (${ext})`;
    case '3G2':
    case '3GP':
    case 'AVI':
    case 'FLV':
    case 'M3U8':
    case 'TS':
    case 'M2TS':
    case 'MTS':
    case 'MOV':
    case 'MKV':
    case 'MP4':
    case 'MPEG':
    case 'MPD':
    case 'MXF':
    case 'OGV':
    case 'WEBM':
    case 'WMV':
      return `Video (${ext})`;
    case 'PDF':
      return `Document (${ext})`;
    case 'AAC':
    case 'AIFF':
    case 'AMR':
    case 'FLAC':
    case 'M4A':
    case 'MP3':
    case 'OGG':
    case 'OPUS':
    case 'WAV':
      return `Audio (${ext})`;
    default:
      return `Raw (${ext})`;
  }
};

const getFileExtension = (url: string) => url.split(/[#?]/)[0].split('.').pop().trim();

export const isRawFile = (filename: string) => {
  return normalizedContentType(getFileExtension(filename)).includes('Raw');
};

export const isVideo = (url: string) => {
  const extension = url.split(/[#?]/)[0].split('.').pop().trim().toUpperCase();
  return normalizedContentType(extension) === `Video (${extension})`;
};

export const getVideoThumbnail = (url: string) => {
  const setFirstFrame = replace(url, '/upload/', '/upload/so_0.0/');
  return replace(setFirstFrame, 'mp4', 'jpg');
};

export function getThumbnailImageFromDiscoverItemCard(itemCard: DiscoverItemCard) {
  const imageUrl = itemCard.thumbnailImageUrl;
  if (imageUrl) {
    if (isVideo(imageUrl)) {
      return getVideoThumbnail(imageUrl);
    }
    return imageUrl.indexOf('/upload/w_') >= 0 ? imageUrl : imageUrl.replace('/upload', '/upload/w_500');
  }

  if (itemCard.discoverType === DiscoverItemType.KIT) {
    return KIT_THUMBNAIL;
  }
  return getThumbnailImageFromTemplate({ type: itemCard.templateType } as Templates);
}

const labelForKitResource = (kitResource: KitResource) => {
  switch (kitResource.type) {
    case DiscoverItemType.KIT:
      return `${kitResource.kit.title} [Kit]`;
    case DiscoverItemType.TEMPLATE:
      return `${kitResource.template.title} [${capitalize(kitResource.template.type)} Template]`;
  }
};

export const kitResourceSelectOptions = (resources): SelectOption[] => {
  return resources.map((resource) => {
    return { value: resource, label: labelForKitResource(resource) };
  });
};

export const featureableDiscoverItemOrderDescriptorsFromFeatureableDiscoverItems = (
  featureableDiscoverItems: FeatureableDiscoverItem[],
): FeatureableDiscoverItemDescriptor[] =>
  // eslint-disable-next-line array-callback-return
  featureableDiscoverItems.map((featureableDiscoverItem): FeatureableDiscoverItemDescriptor => {
    switch (featureableDiscoverItem.type) {
      case DiscoverItemType.HUB:
        return {
          type: DiscoverItemType.HUB,
          id: featureableDiscoverItem.item.id,
        };
      case DiscoverItemType.KIT:
        return {
          type: DiscoverItemType.KIT,
          id: featureableDiscoverItem.item.id,
        };
    }
  });

const labelForHubResource = (hubResource: HubResource) => {
  switch (hubResource.type) {
    case DiscoverItemType.KIT:
      return `${hubResource.kit.title} [Kit]`;
    case DiscoverItemType.TEMPLATE:
      return `${hubResource.template.title} [${capitalize(hubResource.template.type)} Template]`;
  }
};

export const hubResourceSelectOptions = (resources): SelectOption[] => {
  return resources.map((resource) => {
    return { value: resource, label: labelForHubResource(resource) };
  });
};

export const selectOptionForFeatureableDiscoverItem = (
  item: FeatureableDiscoverItem,
): SelectOption<FeatureableDiscoverItemDescriptor> => {
  switch (item.type) {
    case DiscoverItemType.HUB:
      return {
        label: item.item.title,
        value: {
          type: DiscoverItemType.HUB,
          id: item.item.id,
        },
      };
    case DiscoverItemType.KIT:
      return {
        label: item.item.title,
        value: {
          type: DiscoverItemType.KIT,
          id: item.item.id,
        },
      };
  }
};

const sortDiscoverItemsAlpha = (discoverItems: DiscoverItemCard[]): DiscoverItemCard[] => {
  discoverItems.sort((a, b) => {
    const authorA = a.metadata.author?.trim() || 'gloo';

    const authorB = b.metadata.author?.trim() || 'gloo';

    const compareResult = authorA.localeCompare(authorB);
    if (compareResult === 0) {
      const titleA = a.title;
      const titleB = b.title;
      return titleA.localeCompare(titleB);
    }

    return compareResult;
  });

  return discoverItems;
};

// separates discover items into an array of arrays each containing an individual author
export const reduceSortDiscoverItemsByAuthorAndTitle = (discoverItems: DiscoverItemCard[]): DiscoverItemCard[][] => {
  return sortDiscoverItemsAlpha(discoverItems).reduce(
    (acc: { discoverItemsByAuthor: DiscoverItemCard[][]; prevAuthor: string }, currItem) => {
      if (!acc.discoverItemsByAuthor.length) {
        acc.discoverItemsByAuthor = [[currItem]];
        acc.prevAuthor = currItem.metadata.author?.trim().toLowerCase() || 'gloo';
        return acc;
      }
      const currAuthor = currItem.metadata.author?.trim().toLowerCase() || 'gloo';
      if (currAuthor === acc.prevAuthor) {
        acc.discoverItemsByAuthor[acc.discoverItemsByAuthor.length - 1].push(currItem);
        acc.prevAuthor = currAuthor;
        return acc;
      }
      acc.discoverItemsByAuthor.push([currItem]);
      acc.prevAuthor = currAuthor;
      return acc;
    },
    { discoverItemsByAuthor: [], prevAuthor: null },
  ).discoverItemsByAuthor;
};

export function useDiscoverItemAuthorSelect() {
  return async (item: DiscoverItemCard) => {
    switch (item.discoverType) {
      case DiscoverItemType.HUB:
        pushProgressive(InAppRoutes.getString(InAppRoutes.AdminHubEdit, item.itemId));
        break;
      case DiscoverItemType.KIT:
        pushProgressive(InAppRoutes.getString(InAppRoutes.AdminKitEdit, item.itemId));
        break;
      case DiscoverItemType.TEMPLATE:
        pushProgressive(InAppRoutes.getString(InAppRoutes.AdminTemplateEdit, item.itemId));
        break;
    }
  };
}

export const sortSelectOptionByLabel = (a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label);
